import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useStable } from "../context/StableContext";
import api, { errText } from "../lib/api";
import HorseImage from "../components/HorseImage";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { RotateCcw, Loader2, LayoutGrid, List, Trash2, ChevronRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const COUNT_LABELS = {
  deadlines: "scadenze", health_history: "storico sanitario", farrier: "interventi mascalcia",
  therapies: "terapie", therapy_admins: "somministrazioni", horse_requests: "richieste", deadline_batches: "batch scadenze",
};

export default function ArchivePage() {
  const { current, perms } = useStable();
  const navigate = useNavigate();
  const [horses, setHorses] = useState(null);
  const [reason, setReason] = useState("all");
  const [view, setView] = useState(() => localStorage.getItem("equiro_archive_view") || "cards");
  const [del, setDel] = useState(null); // {horse, preview, typed}

  const changeView = (v) => { setView(v); localStorage.setItem("equiro_archive_view", v); };

  const load = useCallback(() => {
    if (!current) return;
    setHorses(null);
    const params = reason === "all" ? {} : { reason };
    api.get(`/stables/${current.id}/archive`, { params }).then((r) => setHorses(r.data));
  }, [current, reason]);

  useEffect(() => { load(); }, [load]);

  const restore = async (h) => {
    try {
      await api.post(`/stables/${current.id}/horses/${h.id}/restore`);
      toast.success(`${h.display_name} ripristinato`);
      load();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };

  const openDelete = async (h) => {
    setDel({ horse: h, preview: null, typed: "" });
    try {
      const r = await api.get(`/stables/${current.id}/horses/${h.id}/delete-preview`);
      setDel((d) => (d && d.horse.id === h.id ? { ...d, preview: r.data } : d));
    } catch (err) { toast.error(errText(err.response?.data?.detail)); setDel(null); }
  };
  const confirmDelete = async () => {
    try {
      await api.delete(`/stables/${current.id}/horses/${del.horse.id}`);
      toast.success(`${del.horse.display_name} eliminato definitivamente`);
      setDel(null); load();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };

  const canDelete = del && del.typed.trim() === (del.horse.display_name || "").trim();
  const previewEntries = del?.preview ? Object.entries(del.preview.counts || {}).filter(([, n]) => n > 0) : [];

  const actions = (h) => (
    <>
      <Button variant="ghost" size="sm" data-testid={`archived-details-${h.id}`} onClick={() => navigate(`/cavalli/${h.id}`)}>Dettagli</Button>
      {perms.canManage && (
        <Button variant="outline" size="sm" onClick={() => restore(h)} data-testid={`restore-btn-${h.id}`}>
          <RotateCcw className="h-4 w-4 mr-1" /> Ripristina
        </Button>
      )}
      {perms.canManage && (
        <Button variant="outline" size="sm" onClick={() => openDelete(h)} data-testid={`delete-archived-${h.id}`}
          className="border-red-500 text-red-600 hover:bg-red-50">
          <Trash2 className="h-4 w-4 mr-1" /> Elimina
        </Button>
      )}
    </>
  );

  return (
    <div className="space-y-6 eq-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono-label text-[#8C532B] text-xs">Storico</p>
          <h1 className="font-display text-2xl lg:text-3xl font-bold text-[#1B3B2B]">Archivio cavalli</h1>
          <p className="text-[#5F6B64] mt-1">I cavalli archiviati conservano tutte le informazioni storiche.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-[#E5DEC9] bg-white overflow-hidden" data-testid="archive-view-toggle">
            <button data-testid="archive-view-cards" onClick={() => changeView("cards")} title="Schede"
              className={`flex items-center gap-1.5 px-3 py-2 text-sm ${view === "cards" ? "bg-[#1B3B2B] text-white" : "text-[#5F6B64]"}`}>
              <LayoutGrid className="h-4 w-4" /> <span className="hidden lg:inline">Schede</span>
            </button>
            <button data-testid="archive-view-compact" onClick={() => changeView("compact")} title="Compatto"
              className={`flex items-center gap-1.5 px-3 py-2 text-sm ${view === "compact" ? "bg-[#1B3B2B] text-white" : "text-[#5F6B64]"}`}>
              <List className="h-4 w-4" /> <span className="hidden lg:inline">Compatto</span>
            </button>
          </div>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="w-[170px] bg-white" data-testid="archive-filter-reason"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i motivi</SelectItem>
              <SelectItem value="uscito_trasferito">Trasferimento</SelectItem>
              <SelectItem value="venduto">Vendita</SelectItem>
              <SelectItem value="deceduto">Decesso</SelectItem>
              <SelectItem value="altro">Altro</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {!horses ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#1B3B2B]" /></div>
      ) : horses.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E5DEC9] py-16 text-center text-[#5F6B64]">Nessun cavallo archiviato.</div>
      ) : view === "compact" ? (
        <div className="bg-white rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1] overflow-hidden" data-testid="archive-compact-list">
          {horses.map((h) => (
            <div key={h.id} data-testid={`archived-horse-${h.id}`} className="flex items-center gap-3 px-3 py-2.5">
              <div className="h-11 w-11 rounded-lg overflow-hidden bg-[#F0EAE1] shrink-0">
                <HorseImage photo={h.photo} alt={h.display_name} className="h-full w-full" fallbackClass="h-full w-full" />
              </div>
              <button onClick={() => navigate(`/cavalli/${h.id}`)} className="min-w-0 flex-1 text-left">
                <p className="font-display font-bold text-[#1B3B2B] truncate leading-tight">{h.display_name}</p>
                <p className="text-xs text-[#5F6B64] truncate">Archiviato il {fmt(h.archive_date)}{h.archive_notes ? ` · ${h.archive_notes}` : ""}</p>
              </button>
              <StatusBadge status={h.archive_reason} />
              <div className="hidden sm:flex gap-1.5 shrink-0">{actions(h)}</div>
              <ChevronRight className="h-4 w-4 text-[#C9BDA0] shrink-0 sm:hidden" onClick={() => navigate(`/cavalli/${h.id}`)} />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {horses.map((h) => (
            <div key={h.id} data-testid={`archived-horse-${h.id}`}
              className="bg-white rounded-xl border border-[#E5DEC9] overflow-hidden flex flex-col">
              <button onClick={() => navigate(`/cavalli/${h.id}`)} className="text-left">
                <div className="h-40 bg-[#F0EAE1] overflow-hidden">
                  <HorseImage photo={h.photo} alt={h.display_name} className="h-full w-full" fallbackClass="h-full w-full" />
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-display font-bold text-lg text-[#1B3B2B] truncate">{h.display_name}</h3>
                      <p className="text-xs text-[#5F6B64] truncate">{h.official_name || "—"}</p>
                    </div>
                    <StatusBadge status={h.archive_reason} />
                  </div>
                  <p className="text-xs text-[#5F6B64] mt-2">Archiviato il {fmt(h.archive_date)}{h.archive_notes ? ` · ${h.archive_notes}` : ""}</p>
                </div>
              </button>
              <div className="px-4 pb-4 mt-auto flex flex-wrap gap-2">{actions(h)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Strong-confirm permanent delete (Archive only) */}
      <Dialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <DialogContent data-testid="delete-archived-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="font-display text-red-700 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Elimina definitivamente</DialogTitle></DialogHeader>
          {!del?.preview ? (
            <div className="flex items-center gap-2 py-4 text-sm text-[#5F6B64]"><Loader2 className="h-4 w-4 animate-spin" /> Verifica dei dati collegati…</div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[#5F6B64]">
                Stai per eliminare <b className="text-[#1B3B2B]">{del.horse.display_name}</b> e i suoi dati operativi in questa scuderia.
                L'operazione <b>non è reversibile</b>.
              </p>
              <div className="rounded-lg border border-[#E5DEC9] bg-[#FCFAF6] p-3 text-sm" data-testid="delete-preview-box">
                {previewEntries.length === 0 && del.preview.lesson_participant_refs === 0 ? (
                  <p className="text-[#5F6B64]">Nessun record collegato: verrà rimosso solo il cavallo.</p>
                ) : (
                  <>
                    <p className="font-semibold text-[#1B3B2B] mb-1">Verranno eliminati anche:</p>
                    <ul className="list-disc pl-5 text-[#5F6B64] space-y-0.5">
                      {previewEntries.map(([k, n]) => <li key={k} data-testid={`del-count-${k}`}>{n} {COUNT_LABELS[k] || k}</li>)}
                      {del.preview.lesson_participant_refs > 0 && <li>{del.preview.lesson_participant_refs} riferimenti nelle lezioni (il cavallo verrà rimosso dai partecipanti)</li>}
                    </ul>
                  </>
                )}
              </div>
              <p className="text-xs text-[#8C6D4F]">{del.preview.note}</p>
              <div>
                <label className="text-sm text-[#5F6B64]">Per confermare digita il nome del cavallo: <b className="text-[#1B3B2B]">{del.horse.display_name}</b></label>
                <Input className="mt-1.5" data-testid="delete-confirm-name" value={del.typed}
                  onChange={(e) => setDel({ ...del, typed: e.target.value })} placeholder={del.horse.display_name} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDel(null)}>Annulla</Button>
            <Button onClick={confirmDelete} disabled={!canDelete} data-testid="delete-archived-confirm"
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">Elimina definitivamente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
